/**
 * Where a part sits in an exported file.
 *
 * Panel meshes are always emitted in their own frame, unrotated, so a printed
 * part comes out in its canonical orientation. A placement is what puts it back
 * where the design has it. The two formats honour it differently: 3MF records
 * it as a transform on the build item, while STL has no concept of an object
 * and has to bake it into the coordinates.
 */
export interface Placement {
  x: number;
  y: number;
  z: number;
  /** Rotation about the part's own centre, in degrees. */
  rotation: number;
}

export const IDENTITY_PLACEMENT: Placement = { x: 0, y: 0, z: 0, rotation: 0 };

/** Precomputed terms, so a per-vertex transform does no trigonometry. */
export interface PlacementTransform {
  cos: number;
  sin: number;
  x: number;
  y: number;
  z: number;
  /** False when the placement is the identity and can be skipped entirely. */
  active: boolean;
}

export function compilePlacement(placement: Placement | undefined): PlacementTransform {
  if (!placement) return { cos: 1, sin: 0, x: 0, y: 0, z: 0, active: false };

  const angle = (placement.rotation * Math.PI) / 180;
  return {
    cos: Math.cos(angle),
    sin: Math.sin(angle),
    x: placement.x,
    y: placement.y,
    z: placement.z,
    active: placement.x !== 0 || placement.y !== 0 || placement.z !== 0 || placement.rotation !== 0,
  };
}
